import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { QRCodeCanvas } from 'qrcode.react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DownloadIcon from '@mui/icons-material/Download'

import { getEnv } from '@/utils/helpers'

type ShareMeetingLinkDialogProps = {
  open: boolean
  onClose: () => void
  meetingLink: {
    name: string
    slug: string
  }
}

const QR_CODE_ID = 'share-meeting-link-qr-code'

export default function ShareMeetingLinkDialog(props: ShareMeetingLinkDialogProps) {
  const { open, onClose, meetingLink } = props
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const bookingUrl = `${getEnv('VITE_MEDCONNECT_PATIENT_URL')}/meeting-links/${meetingLink.slug}/booking`

  const handleCopy = () => {
    navigator.clipboard.writeText(bookingUrl)
    enqueueSnackbar(t('page.meetingLinks.share.linkCopied'), { variant: 'success' })
  }

  const handleDownloadQR = () => {
    const canvas = document.getElementById(QR_CODE_ID) as HTMLCanvasElement
    if (!canvas) return

    const url = canvas.toDataURL('image/png')
    const link = document.createElement('a')
    link.download = `${meetingLink.name}-qr-code.png`
    link.href = url
    link.click()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {t('page.meetingLinks.share.title')} - {meetingLink.name}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ py: 1 }}>
          {/* Booking URL */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('page.meetingLinks.share.bookingUrl')}
            </Typography>
            <TextField
              fullWidth
              value={bookingUrl}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopy} edge="end">
                      <ContentCopyIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Box>

          {/* QR Code */}
          <Stack alignItems="center" spacing={2}>
            <Typography variant="subtitle2">
              {t('page.meetingLinks.share.qrCode')}
            </Typography>
            <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <QRCodeCanvas
                id={QR_CODE_ID}
                value={bookingUrl}
                size={200}
                level="H"
                includeMargin
              />
            </Box>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadQR}
            >
              {t('page.meetingLinks.share.downloadQR')}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
